import { useQuery } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import React, { Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SettingsPageFrame } from '@eluve/blocks';
import {
  Card,
  EluveCardContent,
  EluveCardHeader,
  Heading,
  LoadingIndicator,
  Text,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { UserGroupCodeConfigs } from './UserGroupCodeConfigs';
import { UserGroupUsers } from './UserGroupUsers';

const userGroupNameFragment = graphql(`
  fragment GroupName on UserGroups @_unmask {
    __typename
    id
    name
  }
`);

const getUserGroupNameQuery = graphql(
  `
    query getUserGroupName($tenantId: uuid!, $id: uuid!) {
      userGroupsByPk(tenantId: $tenantId, id: $id) {
        ...GroupName
      }
    }
  `,
  [userGroupNameFragment],
);

const useUserGroupName = (tenantId: string, groupId: string) => {
  const group = useCompleteFragment({
    fragment: userGroupNameFragment,
    key: {
      id: groupId,
    },
    strict: false,
  });

  useQuery(getUserGroupNameQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      tenantId,
      id: groupId,
    },
    skip: Boolean(group),
  });

  return group?.name;
};

const UserGroupDetailsContent: React.FC<{}> = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };
  const { t } = useTranslation();

  const groupName = useUserGroupName(tenantId, userGroupId);

  return (
    <div>
      <div className="grid gap-5">
        <div className="my-2 py-2">
          <Heading>{groupName}</Heading>
        </div>
      </div>

      <ErrorBoundary
        fallback={<div>{t('tenantAdmin.common.thereWasAnError')}</div>}
      >
        <Suspense fallback={<LoadingIndicator size="xl" />}>
          <div className="grid gap-5">
            <Card className="w-full">
              <EluveCardHeader>
                <Text>{t('tenantAdmin.common.users')}</Text>
              </EluveCardHeader>
              <EluveCardContent>
                <UserGroupUsers />
              </EluveCardContent>
            </Card>

            <UserGroupCodeConfigs />
          </div>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

export const UserGroupDetailsPage: React.FC<{}> = () => {
  const { t } = useTranslation();

  return (
    <SettingsPageFrame
      size="wide"
      parentTitle={t('tenantAdmin.userGroups.title')}
      backTo=".."
    >
      <UserGroupDetailsContent />
    </SettingsPageFrame>
  );
};
